import { useState } from "react";
import { api } from "@/lib/api";
import { X, Download, UploadCloud, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

function downloadBlob(data, filename) {
  const url = window.URL.createObjectURL(new Blob([data]));
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
}

// Jun-2026 — Bulk role-permission upload (owner choices: REPLACE semantics
// with a mandatory preview step; supports creating a brand-new role).
export function RoleBulkPermissionsModal({ roles, onClose, onSaved }) {
  const [mode, setMode] = useState("existing"); // existing | new
  const [roleId, setRoleId] = useState("");
  const [newName, setNewName] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const targetOk = mode === "existing" ? !!roleId : !!newName.trim();

  const downloadTemplate = async (prefill) => {
    try {
      const r = await api.get("/roles/permissions-template",
        { params: prefill && roleId ? { role_id: roleId } : {}, responseType: "blob" });
      const role = roles.find(x => x.id === roleId);
      downloadBlob(r.data, prefill && role ? `role-permissions-${role.name}.xlsx` : "role-permissions-template.xlsx");
    } catch { toast.error("Template download failed"); }
  };

  const upload = async (dryRun) => {
    if (!file) { toast.error("Choose the filled .xlsx file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("role_id", mode === "existing" ? roleId : "");
      fd.append("new_role_name", mode === "new" ? newName.trim() : "");
      fd.append("dry_run", dryRun ? "true" : "false");
      const r = await api.post("/roles/permissions-upload", fd);
      if (dryRun) {
        setPreview(r.data);
      } else {
        toast.success(`Permissions applied to '${r.data.role_name}'${r.data.users_propagated ? ` — synced to ${r.data.users_propagated} user(s)` : ""}`);
        onSaved?.(); onClose();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="bulk-permissions-modal">
      <div className="bg-white w-full max-w-3xl max-h-[88vh] flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div>
            <div className="text-sm font-bold text-slate-900">Bulk Permission Upload</div>
            <div className="text-[11px] text-slate-500">The sheet REPLACES the role's full permission set — preview before applying.</div>
          </div>
          <button data-testid="bulk-close-btn" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="overflow-y-auto flex-1 p-4 space-y-4">
          {/* Step 1 — target role */}
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">1 · Target role</div>
            <div className="flex gap-2 mb-2">
              <button type="button" data-testid="bulk-mode-existing" onClick={() => { setMode("existing"); setPreview(null); }}
                className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider border ${mode === "existing" ? "bg-[#1A1D21] border-[#1A1D21] text-white" : "bg-white border-slate-300 text-slate-600"}`}>
                Existing Role
              </button>
              <button type="button" data-testid="bulk-mode-new" onClick={() => { setMode("new"); setPreview(null); }}
                className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider border ${mode === "new" ? "bg-[#F97316] border-[#F97316] text-white" : "bg-white border-slate-300 text-slate-600"}`}>
                New Role
              </button>
            </div>
            {mode === "existing" ? (
              <select data-testid="bulk-role-select" value={roleId} onChange={e => { setRoleId(e.target.value); setPreview(null); }}
                className="w-full border border-slate-300 px-2 py-2 text-sm bg-white">
                <option value="">— Pick a role —</option>
                {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            ) : (
              <input data-testid="bulk-new-role-name" value={newName} onChange={e => { setNewName(e.target.value); setPreview(null); }}
                placeholder="New role name (e.g. Site Accountant)"
                className="w-full border border-slate-300 px-2 py-2 text-sm" />
            )}
          </div>
          {/* Step 2 — template */}
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">2 · Download template</div>
            <div className="flex gap-2 flex-wrap">
              <button type="button" data-testid="bulk-download-blank" onClick={() => downloadTemplate(false)}
                className="flex items-center gap-1.5 px-3 py-2 border border-slate-300 text-xs font-bold text-slate-700 hover:border-[#F97316]">
                <Download size={13} /> Blank Template
              </button>
              {mode === "existing" && (
                <button type="button" data-testid="bulk-download-prefilled" disabled={!roleId} onClick={() => downloadTemplate(true)}
                  className="flex items-center gap-1.5 px-3 py-2 border border-slate-300 text-xs font-bold text-slate-700 hover:border-[#F97316] disabled:opacity-40">
                  <Download size={13} /> Pre-filled from Selected Role
                </button>
              )}
            </div>
            <p className="text-[11px] text-slate-500 mt-1.5">Mark <strong>Y</strong> in the action columns; list granular actions (e.g. <code>backdate, submit</code>) in "Granular Granted". Blank = no access.</p>
          </div>
          {/* Step 3 — upload */}
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">3 · Upload filled sheet</div>
            <input data-testid="bulk-file-input" type="file" accept=".xlsx"
              onChange={e => { setFile(e.target.files?.[0] || null); setPreview(null); }}
              className="text-xs" />
          </div>
          {/* Preview */}
          {preview && (
            <div className="border border-slate-200" data-testid="bulk-preview">
              <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-700">
                Preview — {preview.is_new_role ? `NEW role '${preview.role_name}'` : `'${preview.role_name}'`} · {preview.totals.modules_changed} module(s) change · +{preview.totals.actions_added} / −{preview.totals.actions_removed} actions
              </div>
              {preview.warnings.length > 0 && (
                <div className="px-3 py-2 bg-amber-50 border-b border-amber-100 space-y-0.5" data-testid="bulk-warnings">
                  {preview.warnings.map((w, i) => (
                    <div key={i} className="flex items-start gap-1.5 text-[11px] text-amber-800"><AlertTriangle size={11} className="mt-0.5 shrink-0" /> {w}</div>
                  ))}
                </div>
              )}
              <div className="max-h-56 overflow-y-auto divide-y divide-slate-100">
                {preview.changes.length === 0 && <div className="px-3 py-4 text-center text-xs text-slate-400">No permission changes — sheet matches the current role.</div>}
                {preview.changes.map(c => (
                  <div key={c.module} className="px-3 py-1.5 text-xs flex flex-wrap items-baseline gap-1.5" data-testid={`bulk-change-${c.module}`}>
                    <span className="font-semibold text-slate-800">{c.label}</span>
                    {c.added.map(a => <span key={a} className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-1">+{a}</span>)}
                    {c.removed.map(a => <span key={a} className="text-[10px] bg-red-50 text-red-700 border border-red-200 px-1 line-through">{a}</span>)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="px-4 py-3 border-t border-slate-200 flex justify-end gap-2 bg-slate-50">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-slate-300 text-xs font-bold uppercase">Cancel</button>
          <button type="button" data-testid="bulk-preview-btn" disabled={busy || !targetOk || !file} onClick={() => upload(true)}
            className="px-4 py-2 bg-[#1A1D21] text-white text-xs font-bold uppercase disabled:opacity-40">
            {busy && !preview ? "Checking…" : "Preview Changes"}
          </button>
          <button type="button" data-testid="bulk-apply-btn" disabled={busy || !preview} onClick={() => upload(false)}
            className="px-4 py-2 bg-[#F97316] text-white text-xs font-bold uppercase disabled:opacity-40 flex items-center gap-1.5">
            <CheckCircle2 size={13} /> Apply
          </button>
        </div>
      </div>
    </div>
  );
}
